import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Vault, Info } from "lucide-react";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";
import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateCashDrawerStatus } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";
import { useAuth } from "../../auth/AuthContext";

const CashDrawerSettings = () => {
  const toast = useToast();
  const { user, updateUser, syncSettingsToLocalStorage } = useAuth();
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const [isCashDrawerEnabled, setIsCashDrawerEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (settingsData?.settings) {
      const val = settingsData.settings.is_cash_drawer_open;
      setIsCashDrawerEnabled(val === true || val === 1 || val === "1" || val === "active");
    }
  }, [settingsData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        is_cash_drawer_open: (isCashDrawerEnabled ? "active" : "inactive") as "active" | "inactive",
      };

      await updateCashDrawerStatus(payload);

      if (user?.company) {
        updateUser({ company: { ...user.company, is_cash_drawer_open: isCashDrawerEnabled } });
      }

      toast.success("Cash drawer settings updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
    } catch (error: any) {
      console.error("Failed to update cash drawer settings", error);
      toast.error("Failed to update cash drawer settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading cash drawer settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cash Drawer Settings"
        description="Configure cash drawer behaviour on payment completion"
        variant="card"
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Vault size={18} className="text-white" />
            Cash Drawer Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            <div>
              <SectionHeader title="Cash Drawer Settings" />

              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  When enabled, the cash drawer will automatically open after every successful payment.
                </p>
              </div>

              <ToggleSetting
                label="Auto-Open Cash Drawer"
                description="Automatically open the cash drawer when a payment is completed"
                checked={isCashDrawerEnabled}
                onChange={setIsCashDrawerEnabled}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashDrawerSettings;
